import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  QueryFunctionContext,
  QueryKey
} from '@tanstack/react-query';
import handleError from '../api-calls/format-error';

interface FetchParams {
  [key: string]: any;
}

interface MutationOptions {
  invalidateAll?: boolean;
  invalidateKey?: QueryKey;
}

axios.defaults.baseURL = import.meta.env.VITE_API_URL || '/api';
axios.defaults.withCredentials = true;

function useFetch<TData = unknown>(
  url: string,
  params?: FetchParams,
  config?: Omit // Ensure config is typed correctly
): UseQueryResult {
  return useQuery({
    queryKey: [url, params],
    queryFn: async ({ queryKey }: QueryFunctionContext) => {
      try {
        const { data } = await axios.get<TData>(url, { params });
        return data;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!url,
    ...config
  }) as UseQueryResult<TData, Error> & { refetch: () => void };
}

function useGenericMutation<
  TData = unknown,
  TError = unknown,
  TVariables = unknown
>(
  func: (data: TVariables) => Promise,
  url: string,
  params?: FetchParams,
  { options }: { options?: MutationOptions } = {}
): UseMutationResult {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: func,
    onMutate: async (data: TVariables) => {
      await queryClient.cancelQueries({ queryKey: [url, params] });
      const previousData = queryClient.getQueryData<TData>([url, params]);
      queryClient.setQueryData([url, params], data);
      return { previousData };
    },
    onError: (_, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([url, params], context.previousData);
      }
    },
    onSettled: () => {
      if (options?.invalidateAll) {
        queryClient.invalidateQueries();
      } else if (options?.invalidateKey) {
        queryClient.invalidateQueries({ queryKey: options.invalidateKey });
      }
      queryClient.invalidateQueries({ queryKey: [url, params] });
    }
  });
}

function usePatch<TData = unknown, TError = unknown, TVariables = unknown>(
  url: string,
  params?: FetchParams,
  { options }: { options?: MutationOptions } = {}
): UseMutationResult {
  return useGenericMutation<TData, TError, TVariables>(
    (data) => axios.patch<TData>(url, data),
    url,
    params,
    { options }
  );
}

function usePost<TData = unknown, TError = unknown, TVariables = unknown>(
  url: string,
  params?: FetchParams,
  { options }: { options?: MutationOptions } = {}
): UseMutationResult {
  return useGenericMutation<TData, TError, TVariables>(
    (data) => axios.post<TData>(url, data),
    url,
    params,
    { options }
  );
}

function formatQueryError<TError>(context: UseQueryResult): string | null {
  return context.isError ? handleError(context.error) : null;
}

export { useFetch, usePost, usePatch, formatQueryError };
